// @ts-nocheck
import { JxsComponent } from "@jxs/component";
import { JxsSwiperSlideChange } from '@jxs/utilities';

const Swiper = /** @type {any} */ (window).Swiper;

/**
 * @typedef {object} JxsCarouselCardsRefs
 * @property {HTMLButtonElement} prevButton
 * @property {HTMLButtonElement} nextButton
 * 
 * @extends {JxsComponent<JxsCarouselCardsRefs>}
 */
class JxsCarouselCards extends JxsComponent {

  /**
   * @type {Swiper}
   */
  #swiper = null;

  /**
   * @type {JxsSwiperSlideChange}
   */
  #swiperSlideChange = null;

  constructor() {
    super();
    this.autoplayNextVideo = this.getDataset('autoplayNextVideo') || false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.initSwiper();
    if (this.autoplayNextVideo) {
      const firstVideo = this.getVideoBySlide(this.#swiper.slides[this.#swiper.activeIndex]);
      if (firstVideo) {
        firstVideo.autoplay = true;
        firstVideo.pauseOnLeave = false;
      }
      this.#swiper.slides.forEach(slide => {
        const video = this.getVideoBySlide(slide);
        video?.addEventListener('ended', this.#handleSlideVideoEnded);
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.autoplayNextVideo) {
      this.#swiper.slides.forEach(slide => {
        this.getVideoBySlide(slide)?.removeEventListener('ended', this.#handleSlideVideoEnded);
      });
    }
    this.#swiperSlideChange?.destroy();
    this.#swiper.destroy(true, true);
  }

  getVideoBySlide(slideEl) {
    return slideEl.querySelector('.carousel-card__media jxs-video');
  }

  initSwiper() {

    /** @type {boolean | object} */
    let autoplay = false;
    if (this.dataset.autoplaySec && !this.autoplayNextVideo) {
      const sec = parseInt(this.dataset.autoplaySec);
      if (sec > 0) {
        autoplay = {
          delay: sec * 1000
        }
      }
    }

    const useTabbar = this.getDataset('useTabbar');
    const slidePerViewOne = useTabbar || this.autoplayNextVideo;

    const tabletBreakpoint = {
      slidesPerView: slidePerViewOne ? 1 : parseFloat(this.dataset.tabletColumns ?? '1'),
      spaceBetween: parseInt(this.dataset.cardGap ?? '0'),
      slidesOffsetBefore: parseInt(this.dataset.paddingLeft ?? '0'),
      slidesOffsetAfter: parseInt(this.dataset.paddingRight ?? '0'),
      enabled: useTabbar || this.dataset.carouselEnabled == 'true'
    }
    const desktopBreakpoint = Object.assign({}, tabletBreakpoint);
    desktopBreakpoint.slidesPerView = slidePerViewOne ? 1 : parseFloat(this.dataset.desktopColumns ?? '1');

    this.#swiper = new Swiper(this.refs.swiper, {
      slidesOffsetBefore: parseInt(this.dataset.mobilePaddingLeft ?? '0'),
      slidesOffsetAfter: parseInt(this.dataset.mobilePaddingRight ?? '0'),
      loop: this.getDataset('loop'),
      loopAdditionalSlides: 1,
      rewind: false,
      // 当slidesPerView有小数，设置为true时，会把最后一块拉到左边，也就是会使右侧留空白
      snapToSlideEdge: false,
      pagination: {
        el: '#' + this.id + ' .swiper-pagination',
        clickable: true
      },
      autoplay: autoplay,
      slidesPerView: slidePerViewOne ? 1 : parseFloat(this.dataset.mobileColumns ?? '1'),
      spaceBetween: parseInt(this.dataset.mobileCardGap ?? '0'),
      enabled: useTabbar || this.dataset.mobileCarouselEnabled == 'true',
      breakpoints: {
        768: tabletBreakpoint,
        1024: desktopBreakpoint
      }
    });
    this.#swiperSlideChange = new JxsSwiperSlideChange(this.#swiper)
    this.#swiperSlideChange.addListener(this.#handleSlideChange);

    this.#updateNavButtonStatus();
  }

  #handleSlideVideoEnded = (e) => {
    if (this.autoplayNextVideo) {
      this.next();
    }
  }

  #handleSlideChange = (swiper, prevIndex, destIndex, activeIndex) => {
    this.#updateNavButtonStatus();
    this.dispatchEvent(new CustomEvent('currentCardChange', {
      detail: {
        index: destIndex
      }
    }));
    if (this.autoplayNextVideo) {
      this.#swiper.slides.forEach(slide => {
        this.getVideoBySlide(slide)?.pause();
      });
      const video = this.getVideoBySlide(this.#swiper.slides[activeIndex]);
      video?.restart();
    }
  }

  #updateNavButtonStatus() {
    if (this.dataset.loop != 'true') {
      this.refs.prevButton.disabled = this.#swiper.isBeginning;
      this.refs.nextButton.disabled = this.#swiper.isEnd;
    }
  }

  next() {
    this.#swiper.slideNext();
  }

  prev() {
    this.#swiper.slidePrev();
  }

  /**
   * @param {number} index 
   */
  go(index) {
    // 在loop模式下，slideToLoop平移不一定按顺序
    // 所以如果导航序号只跳1，就用next和prev，这样
    // 平移会更流畅
    if (index - this.#swiper.realIndex == 1) {
      this.next();
    } else if (this.#swiper.realIndex - index == 1) {
      this.prev();
    } else {
      if (this.getDataset('loop')) {
        this.#swiper.slideToLoop(index);
      } else {
        this.#swiper.slideTo(index);
      }
    }
  }
}
customElements.define('jxs-carousel-cards', JxsCarouselCards);

class JxsCarouselCardsTabbar extends JxsComponent {

  #onCardChange = (/** @type {Event} */ e) => {
    const event = /** @type {CustomEvent<{ index: number }>} */ (e);

    this.items.forEach(item => {
      item.removeAttribute('active')
    })
    this.items[event.detail.index]?.setAttribute('active', '')
  }

  /**
   * @type {JxsCarouselCards | null}
   */
  #carouselCards = null;

  constructor() {
    super();
  }

  get items() {
    return Array.from(this.querySelectorAll('.carousel-tabbar__item'))
  }

  connectedCallback() {
    super.connectedCallback()
    this.#carouselCards = this.closest('jxs-carousel-cards')
    this.#carouselCards?.addEventListener('currentCardChange', this.#onCardChange)
    this.items[0]?.setAttribute('active', '')
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.#carouselCards?.removeEventListener('currentCardChange', this.#onCardChange)
  }

  /**
   * @param {Event} e
   */
  updateCurrentCard(e) {
    const index = this.items.findIndex(item => item == e.target);
    this.#carouselCards?.go(index);
  }
}
customElements.define('jxs-carousel-cards-tabbar', JxsCarouselCardsTabbar);